import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '@store/store';
import { tokenStorage } from '@utils/tokenStorage';
import { USER_ACCESS_TOKEN, USER_REFRESH_TOKEN } from '@constants/auth';

// =============================================================================
// SHARED WARMUP POOL TYPES
// =============================================================================

export type PoolEmailProvider = 'GMAIL' | 'OUTLOOK' | 'SMTP';
export type PoolEmailStatus = 'ACTIVE' | 'PAUSED' | 'DISABLED' | 'WARMING';
export type WarmupProfileType = 'conservative' | 'standard' | 'aggressive';

/**
 * A shared pool email account that belongs to the system (admin-managed)
 * These emails are available for ALL users to warm their emails against
 *
 * Field names match NestJS backend response
 */
export interface SharedPoolEmail {
  id: string;
  email: string;
  provider: PoolEmailProvider;
  status: PoolEmailStatus;

  // SMTP/IMAP config
  smtpHost?: string | null;
  smtpPort?: number | null;
  smtpUsername?: string | null;
  smtpPassword?: string | null;
  imapHost?: string | null;
  imapPort?: number | null;
  imapUsername?: string | null;
  imapPassword?: string | null;

  // OAuth config
  oauthAccessToken?: string | null;
  oauthRefreshToken?: string | null;
  oauthExpiresAt?: string | null;

  // Warmup metrics
  reputationScore: number; // 0-100
  healthScore: number; // 0-100
  lastHealthCheck?: string | null;

  // Daily limits
  dailySendLimit: number;
  dailyReceiveLimit: number;
  sentToday: number;
  receivedToday: number;

  // Status
  pausedAt?: string | null;
  pauseReason?: string | null;

  // Metadata
  addedBy?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;

  // Aggregated data
  _count?: {
    interactions: number;
  };
}

/**
 * Statistics for the shared warmup pool
 */
export interface SharedPoolStats {
  totalEmails: number;
  activeEmails: number;
  totalCapacity: number;
  currentUsage: number;
  avgReputation: number;
  avgInboxPlacement: number;

  // Provider distribution
  providerBreakdown: {
    gmail: number;
    outlook: number;
    smtp: number;
  };

  // Status breakdown
  statusBreakdown: {
    active: number;
    warming: number;
    paused: number;
    failed: number;
    retired: number;
  };

  // Health metrics
  healthScore: number; // 0-100
  lastUpdated: string;
}

/**
 * Configuration for the warmup pool behavior
 * Field names match NestJS backend response
 */
export interface PoolConfiguration {
  id: string;

  // Pool settings
  isEnabled: boolean;
  minPoolSize: number;
  targetPoolSize: number;
  maxDailyInteractionsPerUser: number;

  // Quality thresholds
  minReputationScore: number;
  minHealthScore: number;

  // Auto-pause settings
  autoPauseOnBounce: boolean;
  bounceThreshold: number;
  autoPauseOnSpam: boolean;
  spamThreshold: number;

  // Scheduling
  warmupStartHour: number;
  warmupEndHour: number;
  warmupTimezone: string;

  createdAt: string;
  updatedAt: string;
}

/**
 * User's warmup status relative to the shared pool
 */
export interface UserWarmupStatus {
  userId: string;
  userEmailCount: number;
  poolEmailsAvailable: number;
  warmupInteractionsToday: number;
  warmupInteractionsTotal: number;
  estimatedWarmupCompletion: string | null;
  isPoolEnabled: boolean;
}

/**
 * A warmup interaction record between a user email and pool email
 */
export interface WarmupInteraction {
  id: string;
  userEmailId: string;
  userEmail: string;
  poolEmailId: string;
  poolEmail: string;
  direction: 'sent' | 'received';
  type: 'warmup' | 'reply' | 'open';
  timestamp: string;
  success: boolean;
  metadata?: {
    subject?: string;
    opened?: boolean;
    replied?: boolean;
    replyTimestamp?: string;
  };
}

/**
 * Bulk import result
 */
export interface BulkImportResult {
  total: number;
  successful: number;
  failed: number;
  errors: Array<{
    email: string;
    error: string;
  }>;
}

// =============================================================================
// DTO TYPES FOR API REQUESTS
// =============================================================================

export interface AddPoolEmailDto {
  email: string;
  provider: PoolEmailProvider;
  notes?: string;

  // SMTP/IMAP credentials (for SMTP provider)
  smtpHost?: string;
  smtpPort?: number;
  smtpUsername?: string;
  smtpPassword?: string;
  imapHost?: string;
  imapPort?: number;
  imapUsername?: string;
  imapPassword?: string;

  // OAuth credentials (for Gmail/Outlook)
  oauthAccessToken?: string;
  oauthRefreshToken?: string;
  oauthExpiresAt?: string;

  // Limits
  dailySendLimit?: number;
  dailyReceiveLimit?: number;
}

export interface UpdatePoolEmailDto {
  status?: PoolEmailStatus;
  warmupProfile?: WarmupProfileType;
  dailyCapacity?: number;
  isAvailableForPool?: boolean;
  notes?: string;
  tags?: string[];
}

export interface BulkImportPoolEmailsDto {
  emails: Array<{
    email: string;
    provider: PoolEmailProvider;
    warmupProfile?: WarmupProfileType;
    smtpCredentials?: {
      smtpHost: string;
      smtpPort: number;
      imapHost: string;
      imapPort: number;
      username: string;
      password: string;
    };
  }>;
}

export interface UpdatePoolConfigurationDto {
  minPoolSize?: number;
  targetPoolSize?: number;
  maxDailyEmailsPerUser?: number;
  warmupIntervalMinutes?: number;
  replyRateTarget?: number;
  autoReplyEnabled?: boolean;
  replyDelayMinMinutes?: number;
  replyDelayMaxMinutes?: number;
  minReputationForPool?: number;
  pauseOnReputationBelow?: number;
  retireOnFailuresCount?: number;
  activeHoursStart?: string;
  activeHoursEnd?: string;
  activeWeekdays?: number[];
  timezone?: string;
}

// =============================================================================
// API SLICE
// =============================================================================

// In development, use Vite proxy (empty baseUrl = relative URLs)
// In production, use the EMAIL_API_URL environment variable
const getEmailApiBaseUrl = () => {
  if (import.meta.env.DEV) {
    return ''; // Use relative URLs to go through Vite proxy
  }
  return import.meta.env.VITE_EMAIL_API_URL || '';
};

// Base query for warmup pool API
const warmupPoolBaseQuery = fetchBaseQuery({
  baseUrl: getEmailApiBaseUrl(),
  prepareHeaders: (headers) => {
    const token = tokenStorage.getToken(USER_ACCESS_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Wrapper that handles 401 errors by attempting token refresh
const warmupPoolBaseQueryWithRefresh: typeof warmupPoolBaseQuery = async (args, api, extraOptions) => {
  let result = await warmupPoolBaseQuery(args, api, extraOptions);

  // If 401, try to refresh the token
  if (result?.error?.status === 401) {
    const refreshToken = tokenStorage.getToken(USER_REFRESH_TOKEN);
    if (refreshToken) {
      const refreshResult = await fetch(`${import.meta.env.VITE_APP_API_URL || '/api'}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (refreshResult.ok) {
        const data = await refreshResult.json();
        if (data.access) {
          tokenStorage.setToken(USER_ACCESS_TOKEN, data.access);
          if (data.refresh) {
            tokenStorage.setToken(USER_REFRESH_TOKEN, data.refresh);
          }
          result = await warmupPoolBaseQuery(args, api, extraOptions);
        }
      }
    }
  }

  return result;
};

export const warmupPoolApi = createApi({
  reducerPath: 'warmupPoolApi',
  baseQuery: warmupPoolBaseQueryWithRefresh,
  tagTypes: ['SharedPoolEmail', 'SharedPoolStats', 'PoolConfiguration', 'UserWarmupStatus', 'WarmupInteraction'],
  endpoints: (builder) => ({
    // =========================================================================
    // SHARED POOL EMAIL MANAGEMENT (Admin)
    // =========================================================================

    /**
     * Get all shared pool emails (admin only)
     */
    getSharedPoolEmails: builder.query<SharedPoolEmail[], {
      status?: PoolEmailStatus;
      provider?: PoolEmailProvider;
      search?: string;
    } | void>({
      query: (params) => ({
        url: '/admin/shared-pool/emails',
        params: params || {},
      }),
      providesTags: ['SharedPoolEmail'],
    }),

    /**
     * Get a single shared pool email by ID
     */
    getSharedPoolEmail: builder.query<SharedPoolEmail, string>({
      query: (id) => `/admin/shared-pool/emails/${id}`,
      providesTags: ['SharedPoolEmail'],
    }),

    /**
     * Add a new email to the shared pool
     */
    addPoolEmail: builder.mutation<SharedPoolEmail, AddPoolEmailDto>({
      query: (data) => ({
        url: '/admin/shared-pool/emails',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SharedPoolEmail', 'SharedPoolStats'],
    }),

    /**
     * Update a shared pool email
     */
    updatePoolEmail: builder.mutation<SharedPoolEmail, { id: string; data: UpdatePoolEmailDto }>({
      query: ({ id, data }) => ({
        url: `/admin/shared-pool/emails/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SharedPoolEmail', 'SharedPoolStats'],
    }),

    /**
     * Delete a shared pool email
     */
    deletePoolEmail: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/shared-pool/emails/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SharedPoolEmail', 'SharedPoolStats'],
    }),

    /**
     * Bulk import emails to the shared pool
     */
    bulkImportPoolEmails: builder.mutation<BulkImportResult, BulkImportPoolEmailsDto>({
      query: (data) => ({
        url: '/admin/shared-pool/emails/bulk-import',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SharedPoolEmail', 'SharedPoolStats'],
    }),

    /**
     * Import pool emails from CSV
     */
    importPoolEmailsCSV: builder.mutation<BulkImportResult, FormData>({
      query: (formData) => ({
        url: '/admin/shared-pool/emails/import-csv',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['SharedPoolEmail', 'SharedPoolStats'],
    }),

    /**
     * Verify and test a pool email's connectivity
     */
    verifyPoolEmail: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({
        url: `/admin/shared-pool/emails/${id}/test-connection`,
        method: 'POST',
      }),
      invalidatesTags: ['SharedPoolEmail'],
    }),

    /**
     * Bulk update status for multiple pool emails
     */
    bulkUpdatePoolEmailStatus: builder.mutation<{ updated: number }, {
      ids: string[];
      status: PoolEmailStatus;
    }>({
      query: (data) => ({
        url: '/admin/shared-pool/emails/bulk-status',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['SharedPoolEmail', 'SharedPoolStats'],
    }),

    // =========================================================================
    // POOL STATISTICS & HEALTH
    // =========================================================================

    /**
     * Get overall pool statistics (user-facing endpoint)
     */
    getSharedPoolStats: builder.query<SharedPoolStats, void>({
      query: () => '/warmup-pool/pool-stats',
      providesTags: ['SharedPoolStats'],
    }),

    /**
     * Get pool health report with recommendations
     */
    getPoolHealthReport: builder.query<{
      healthScore: number;
      issues: Array<{
        severity: 'critical' | 'warning' | 'info';
        message: string;
        recommendation: string;
      }>;
      recommendations: string[];
    }, void>({
      query: () => '/admin/shared-pool/health',
      providesTags: ['SharedPoolStats'],
    }),

    // =========================================================================
    // POOL CONFIGURATION
    // =========================================================================

    /**
     * Get pool configuration
     */
    getPoolConfiguration: builder.query<PoolConfiguration, void>({
      query: () => '/admin/shared-pool/configuration',
      providesTags: ['PoolConfiguration'],
    }),

    /**
     * Update pool configuration
     */
    updatePoolConfiguration: builder.mutation<PoolConfiguration, UpdatePoolConfigurationDto>({
      query: (data) => ({
        url: '/admin/shared-pool/configuration',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['PoolConfiguration'],
    }),

    // =========================================================================
    // USER-FACING ENDPOINTS (Non-Admin)
    // =========================================================================

    /**
     * Get current user's warmup status relative to shared pool
     */
    getUserWarmupStatus: builder.query<UserWarmupStatus, void>({
      query: () => '/warmup-pool/my-status',
      providesTags: ['UserWarmupStatus'],
    }),

    /**
     * Get warmup interactions for current user
     */
    getUserWarmupInteractions: builder.query<WarmupInteraction[], {
      limit?: number;
      offset?: number;
      startDate?: string;
      endDate?: string;
    } | void>({
      query: (params) => ({
        url: '/warmup-pool/my-interactions',
        params: params || {},
      }),
      providesTags: ['WarmupInteraction'],
    }),

    /**
     * Enable/disable pool warmup for a user's email account
     */
    togglePoolWarmupForAccount: builder.mutation<void, {
      emailAccountId: string;
      enabled: boolean;
    }>({
      query: (data) => ({
        url: `/warmup-pool/accounts/${data.emailAccountId}/toggle`,
        method: 'POST',
        body: { enabled: data.enabled },
      }),
      invalidatesTags: ['UserWarmupStatus'],
    }),

    // =========================================================================
    // OAUTH & CREDENTIALS MANAGEMENT FOR POOL EMAILS
    // =========================================================================

    /**
     * Get OAuth/credentials status for a pool email
     */
    getPoolEmailOAuthStatus: builder.query<{
      hasOAuth: boolean;
      hasSmtpCredentials: boolean;
      tokenExpired: boolean;
      expiresAt: string | null;
    }, string>({
      query: (id) => `/admin/shared-pool/emails/${id}/auth/status`,
      providesTags: ['SharedPoolEmail'],
    }),

    /**
     * Initiate Google OAuth for a Gmail pool email
     * Returns URL to redirect user to
     */
    initiatePoolEmailGoogleOAuth: builder.query<{ url: string }, string>({
      query: (id) => `/admin/shared-pool/emails/${id}/auth/google`,
    }),

    /**
     * Connect Outlook pool email using Client Credentials flow.
     * This is the recommended method - no user redirect needed.
     * Microsoft requires Application permissions for IMAP/SMTP access.
     */
    connectOutlookPoolEmail: builder.mutation<{ success: boolean; email: string }, string>({
      query: (id) => ({
        url: `/admin/shared-pool/emails/${id}/auth/outlook/connect`,
        method: 'POST',
      }),
      invalidatesTags: ['SharedPoolEmail'],
    }),

    /**
     * Legacy: Initiate Outlook OAuth for an Outlook pool email
     * @deprecated Use connectOutlookPoolEmail instead
     */
    initiatePoolEmailOutlookOAuth: builder.query<{ url: string }, string>({
      query: (id) => `/admin/shared-pool/emails/${id}/auth/outlook`,
    }),

    /**
     * Set SMTP/IMAP credentials for an SMTP provider pool email (MXRoute etc.)
     */
    setPoolEmailSmtpCredentials: builder.mutation<{ success: boolean; email: string }, {
      id: string;
      credentials: {
        smtpHost: string;
        smtpPort: number;
        smtpUsername: string;
        smtpPassword: string;
        imapHost: string;
        imapPort: number;
        imapUsername?: string;
        imapPassword?: string;
      };
    }>({
      query: ({ id, credentials }) => ({
        url: `/admin/shared-pool/emails/${id}/credentials`,
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['SharedPoolEmail'],
    }),

    /**
     * Refresh OAuth token for a pool email
     */
    refreshPoolEmailToken: builder.mutation<{ success: boolean; newExpiresAt: string }, string>({
      query: (id) => ({
        url: `/admin/shared-pool/emails/${id}/auth/refresh`,
        method: 'POST',
      }),
      invalidatesTags: ['SharedPoolEmail'],
    }),

    /**
     * Legacy - Get Gmail OAuth URL for adding pool email
     * @deprecated Use initiatePoolEmailGoogleOAuth instead
     */
    getPoolGmailOAuthUrl: builder.query<{ url: string }, void>({
      query: () => '/admin/shared-pool/oauth/gmail/url',
    }),

    /**
     * Legacy - Get Outlook OAuth URL for adding pool email
     * @deprecated Use initiatePoolEmailOutlookOAuth instead
     */
    getPoolOutlookOAuthUrl: builder.query<{ url: string }, void>({
      query: () => '/admin/shared-pool/oauth/outlook/url',
    }),
  }),
});

// =============================================================================
// EXPORT HOOKS
// =============================================================================

export const {
  // Admin - Pool Email Management
  useGetSharedPoolEmailsQuery,
  useGetSharedPoolEmailQuery,
  useAddPoolEmailMutation,
  useUpdatePoolEmailMutation,
  useDeletePoolEmailMutation,
  useBulkImportPoolEmailsMutation,
  useImportPoolEmailsCSVMutation,
  useVerifyPoolEmailMutation,
  useBulkUpdatePoolEmailStatusMutation,

  // Admin - Statistics & Health
  useGetSharedPoolStatsQuery,
  useGetPoolHealthReportQuery,

  // Admin - Configuration
  useGetPoolConfigurationQuery,
  useUpdatePoolConfigurationMutation,

  // User-facing
  useGetUserWarmupStatusQuery,
  useGetUserWarmupInteractionsQuery,
  useTogglePoolWarmupForAccountMutation,

  // OAuth & Credentials Management
  useGetPoolEmailOAuthStatusQuery,
  useLazyInitiatePoolEmailGoogleOAuthQuery,
  useConnectOutlookPoolEmailMutation,
  useLazyInitiatePoolEmailOutlookOAuthQuery, // deprecated
  useSetPoolEmailSmtpCredentialsMutation,
  useRefreshPoolEmailTokenMutation,

  // Legacy OAuth (deprecated)
  useGetPoolGmailOAuthUrlQuery,
  useGetPoolOutlookOAuthUrlQuery,
} = warmupPoolApi;
